/**
 * Browse component registry — maps provider_type to lazy-loaded component.
 *
 * Providers without a browse step (e.g. S3) simply have no entry.
 * StepBrowse looks up this registry and falls back to manual input.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { BrowseComponentProps } from './types';

type BrowseComponentEntry = () => Promise<{ default: ComponentType<BrowseComponentProps> }>;

const registry: Record<string, BrowseComponentEntry> = {
  sharepoint: () => import('./SharePointBrowser').then((m) => ({ default: m.SharePointBrowser })),
  git: () => import('./GitHubRepoBrowser').then((m) => ({ default: m.GitHubRepoBrowser })),
  onedrive: () => import('./OneDriveBrowser').then((m) => ({ default: m.OneDriveBrowser })),
  google_drive: () => import('./GoogleDriveBrowser').then((m) => ({ default: m.GoogleDriveBrowser })),
};

// Cache of lazy components — MUST be stable across renders, otherwise
// React treats each render as a new component type and remounts the subtree,
// which resets browser state (search term, selected items, loaded sites, ...).
const lazyCache: Record<string, LazyExoticComponent<ComponentType<BrowseComponentProps>>> = {};

/**
 * Get a lazy-loaded browse component for the given provider type.
 * Returns null if the provider has no browse component.
 */
export function getBrowseComponent(providerType: string): ComponentType<BrowseComponentProps> | null {
  const loader = registry[providerType];
  if (!loader) return null;
  if (!lazyCache[providerType]) {
    lazyCache[providerType] = lazy(loader);
  }
  return lazyCache[providerType];
}

